// src/pages/MisReclamos.jsx
import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useReclamos } from "../hooks/useReclamos";
import { useAuth } from "../hooks/useAuth";
import { ROUTES, CATEGORIAS_CONFIG, ESTADOS_CONFIG } from "../constants";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { Badge } from "../components/common/Badge";
import { Loading } from "../components/common/Loading";
import { Select } from "../components/common/Select";

export default function MisReclamos() {
  const { userData } = useAuth();
  const { reclamos, loading } = useReclamos(false);
  const navigate = useNavigate();
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [direcciones, setDirecciones] = useState({});

  // Obtener dirección aproximada para cada reclamo con coordenadas
  useEffect(() => {
    const fetchDirecciones = async () => {
      const conCoords = reclamos.filter(r => r.latitud && r.longitud);
      await Promise.all(conCoords.map(async (r) => {
        try {
          const geoRes = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${r.latitud}&lon=${r.longitud}&zoom=18&addressdetails=1`,
            { headers: { 'User-Agent': 'ReclamosApp/1.0' } }
          );
          if (geoRes.ok) {
            const geoData = await geoRes.json();
            const addr = geoData.address || {};
            const direccionFormateada = [
              addr.road,
              addr.house_number,
              addr.suburb || addr.neighbourhood,
              addr.city || addr.town || addr.village,
              addr.state
            ].filter(Boolean).join(', ');
            setDirecciones(prev => ({ ...prev, [r.id]: direccionFormateada || geoData.display_name }));
          } else {
            setDirecciones(prev => ({ ...prev, [r.id]: 'Dirección no disponible' }));
          }
        } catch (err) {
          setDirecciones(prev => ({ ...prev, [r.id]: 'Dirección no disponible' }));
        }
      }));
    };

    if (reclamos.length > 0) fetchDirecciones();
  }, [reclamos]);

  // Filtrar reclamos
  const reclamosFiltrados = useMemo(() => {
    return reclamos.filter(reclamo => {
      const matchEstado = !filtroEstado || reclamo.estado === filtroEstado;
      const matchCategoria = !filtroCategoria || reclamo.categoria === filtroCategoria;
      return matchEstado && matchCategoria;
    });
  }, [reclamos, filtroEstado, filtroCategoria]);

  // Calcular estadísticas
  const estadisticas = useMemo(() => {
    const stats = {
      total: reclamos.length,
      porEstado: {},
      porCategoria: {},
      tiempoPromedio: 0
    };

    // Por estado
    ESTADOS_CONFIG.forEach(estado => {
      stats.porEstado[estado.value] = reclamos.filter(r => r.estado === estado.value).length;
    });

    // Por categoría
    CATEGORIAS_CONFIG.forEach(categoria => {
      stats.porCategoria[categoria.value] = reclamos.filter(r => r.categoria === categoria.value).length;
    });

    // Calcular tiempo promedio de resolución (solo resueltos)
    const resueltos = reclamos.filter(r => r.estado === "resuelto");
    if (resueltos.length > 0) {
      const tiempos = resueltos.map(r => {
        const creacion = new Date(r.fecha_creacion);
        const ahora = new Date();
        return Math.floor((ahora - creacion) / (1000 * 60 * 60 * 24)); // días
      });
      stats.tiempoPromedio = Math.round(tiempos.reduce((a, b) => a + b, 0) / tiempos.length);
    }

    return stats;
  }, [reclamos]);


  const getEstadoVariant = (estado) => {
    if (estado === "resuelto") return "success";
    if (estado === "en_progreso") return "info";
    return "warning";
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("es-AR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "Fecha inválida";
    }
  };

  if (loading) {
    return <Loading text="Cargando tus reclamos..." />;
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "32px",
        flexWrap: "wrap",
        gap: "16px"
      }}>
        <div>
          <h1 style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#1e293b",
            margin: "0 0 8px 0"
          }}>
            📋 Mis Reclamos
          </h1>
          <p style={{
            fontSize: "16px",
            color: "#64748b",
            margin: 0
          }}>
            {userData?.email} • {reclamos.length} reclamo{reclamos.length !== 1 ? 's' : ''} registrado{reclamos.length !== 1 ? 's' : ''}
          </p>
        </div>

        <Button
          variant="primary"
          size="lg"
          icon="✚"
          onClick={() => navigate(ROUTES.HOME)}
        >
          Crear Nuevo Reclamo
        </Button>
      </div>

      {/* Estadísticas Generales */}
      {reclamos.length > 0 && (
        <>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginBottom: "32px"
          }}>
            {/* Total de Reclamos */}
            <Card padding="24px">
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "48px",
                  fontWeight: "700",
                  color: "#00a680",
                  marginBottom: "8px"
                }}>
                  {estadisticas.total}
                </div>
                <div style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#64748b"
                }}>
                  Total de Reclamos
                </div>
              </div>
            </Card>

            {/* Pendientes */}
            <Card padding="24px">
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "48px",
                  fontWeight: "700",
                  color: "#fbbf24",
                  marginBottom: "8px"
                }}>
                  {estadisticas.porEstado.pendiente || 0}
                </div>
                <div style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#64748b"
                }}>
                  ⏸️ Pendientes
                </div>
              </div>
            </Card>

            {/* En Progreso */}
            <Card padding="24px">
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "48px",
                  fontWeight: "700",
                  color: "#3b82f6",
                  marginBottom: "8px"
                }}>
                  {estadisticas.porEstado.en_progreso || 0}
                </div>
                <div style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#64748b"
                }}>
                  ⏳ En Progreso
                </div>
              </div>
            </Card>

            {/* Resueltos */}
            <Card padding="24px">
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "48px",
                  fontWeight: "700",
                  color: "#10b981",
                  marginBottom: "8px"
                }}>
                  {estadisticas.porEstado.resuelto || 0}
                </div>
                <div style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#64748b"
                }}>
                  ✅ Resueltos
                </div>
              </div>
            </Card>

            {/* Tiempo Promedio */}
            {estadisticas.tiempoPromedio > 0 && (
              <Card padding="24px">
                <div style={{ textAlign: "center" }}>
                  <div style={{
                    fontSize: "48px",
                    fontWeight: "700",
                    color: "#8b5cf6",
                    marginBottom: "8px"
                  }}>
                    {estadisticas.tiempoPromedio}
                  </div>
                  <div style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#64748b"
                  }}>
                    📊 Días Promedio
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Gráfico de Categorías */}
          <Card padding="24px" style={{ marginBottom: "32px" }}>
            <h3 style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1e293b",
              margin: "0 0 20px 0"
            }}>
              📊 Distribución por Categoría
            </h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "16px"
            }}>
              {CATEGORIAS_CONFIG.map(categoria => {
                const count = estadisticas.porCategoria[categoria.value] || 0;
                const percentage = estadisticas.total > 0
                  ? Math.round((count / estadisticas.total) * 100)
                  : 0;

                return (
                  <div
                    key={categoria.value}
                    style={{
                      padding: "16px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                      textAlign: "center"
                    }}
                  >
                    <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                      {categoria.icon}
                    </div>
                    <div style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      color: "#1e293b",
                      marginBottom: "4px"
                    }}>
                      {count}
                    </div>
                    <div style={{
                      fontSize: "13px",
                      color: "#64748b",
                      marginBottom: "4px",
                      fontWeight: "500"
                    }}>
                      {categoria.label}
                    </div>
                    <div style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#94a3b8"
                    }}>
                      {percentage}%
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Filtros */}
          <Card padding="24px" style={{ marginBottom: "32px" }}>
            <h3 style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1e293b",
              margin: "0 0 16px 0"
            }}>
              🔍 Filtrar Reclamos
            </h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px"
            }}>
              <Select
                label="Por Estado"
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                options={[
                  { value: "", label: "Todos los estados" },
                  ...ESTADOS_CONFIG.map(e => ({ value: e.value, label: e.label }))
                ]}
              />

              <Select
                label="Por Categoría"
                value={filtroCategoria}
                onChange={(e) => setFiltroCategoria(e.target.value)}
                options={[
                  { value: "", label: "Todas las categorías" },
                  ...CATEGORIAS_CONFIG.map(c => ({ value: c.value, label: `${c.icon} ${c.label}` }))
                ]}
              />

              {(filtroEstado || filtroCategoria) && (
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => {
                      setFiltroEstado("");
                      setFiltroCategoria("");
                    }}
                  >
                    Limpiar Filtros
                  </Button>
                </div>
              )}
            </div>
            <div style={{
              marginTop: "12px",
              fontSize: "14px",
              color: "#64748b"
            }}>
              Mostrando {reclamosFiltrados.length} de {reclamos.length} reclamos
            </div>
          </Card>
        </>
      )}

      {/* Lista de reclamos */}
      {reclamos.length === 0 ? (
        <Card padding="60px 40px">
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "64px", marginBottom: "16px" }}>📝</div>
            <h2 style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#1e293b",
              marginBottom: "12px"
            }}>
              No tienes reclamos aún
            </h2>
            <p style={{
              fontSize: "16px",
              color: "#64748b",
              marginBottom: "24px"
            }}>
              Crea tu primer reclamo desde el mapa para comenzar
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate(ROUTES.HOME)}
            >
              Ir al Mapa
            </Button>
          </div>
        </Card>
      ) : reclamosFiltrados.length === 0 ? (
        <Card padding="60px 40px">
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "64px", marginBottom: "16px" }}>🔍</div>
            <h2 style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#1e293b",
              marginBottom: "12px"
            }}>
              No hay reclamos con estos filtros
            </h2>
            <p style={{
              fontSize: "16px",
              color: "#64748b",
              marginBottom: "24px"
            }}>
              Intenta ajustar los filtros para ver más resultados
            </p>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => {
                setFiltroEstado("");
                setFiltroCategoria("");
              }}
            >
              Limpiar Filtros
            </Button>
          </div>
        </Card>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
          gap: "24px"
        }}>
          {reclamosFiltrados.map((reclamo) => {
            const categoria = CATEGORIAS_CONFIG.find(c => c.value === reclamo.categoria);
            const estadoConfig = ESTADOS_CONFIG.find(e => e.value === reclamo.estado);
            const diasTranscurridos = Math.floor(
              (new Date() - new Date(reclamo.fecha_creacion)) / (1000 * 60 * 60 * 24)
            );

            return (
              <Card key={reclamo.id} hover padding="0" style={{ overflow: "hidden" }}>
                {/* Header de la tarjeta con estado */}
                <div style={{
                  padding: "20px 24px",
                  background: estadoConfig?.value === "resuelto"
                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                    : estadoConfig?.value === "en_progreso"
                      ? "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                      : "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                  borderBottom: "1px solid #e2e8f0"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}>
                      <span style={{ fontSize: "24px" }}>{categoria?.icon || "📌"}</span>
                      <span style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#334155"
                      }}>
                        {categoria?.label || reclamo.categoria}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {reclamo.promedio_calificacion > 0 && (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#92400e",
                          backgroundColor: "rgba(255, 255, 255, 0.5)",
                          padding: "2px 8px",
                          borderRadius: "12px"
                        }}>
                          <span>★</span>
                          <span>{reclamo.promedio_calificacion.toFixed(1)}</span>
                        </div>
                      )}
                      <Badge variant={getEstadoVariant(reclamo.estado)}>
                        {estadoConfig?.label || reclamo.estado}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Contenido de la tarjeta */}
                <div style={{ padding: "24px" }}>
                  <h3 style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "#1e293b",
                    margin: "0 0 12px 0",
                    lineHeight: "1.3"
                  }}>
                    {reclamo.titulo}
                  </h3>

                  <p style={{
                    fontSize: "15px",
                    color: "#64748b",
                    margin: "0 0 20px 0",
                    lineHeight: "1.5",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}>
                    {reclamo.descripcion}
                  </p>

                  {/* Detalles */}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    paddingTop: "20px",
                    borderTop: "1px solid #e2e8f0"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: "14px"
                    }}>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "#64748b"
                      }}>
                        <span>📍</span>
                        <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                          {direcciones[reclamo.id] || `${reclamo.latitud?.toFixed(4)}, ${reclamo.longitud?.toFixed(4)}`}
                        </span>
                      </div>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        color: "#64748b"
                      }}>
                        <span>🕐</span>
                        <span>{diasTranscurridos} día{diasTranscurridos !== 1 ? 's' : ''}</span>
                      </div>
                    </div>

                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "13px",
                      color: "#64748b"
                    }}>
                      <span>📅</span>
                      <span>{formatDate(reclamo.fecha_creacion)}</span>
                    </div>
                  </div>
                </div>

                {/* Footer con acción */}
                <div style={{
                  padding: "16px 24px",
                  backgroundColor: "#f8fafc",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: estadoConfig?.value === "resuelto"
                      ? "#166534"
                      : estadoConfig?.value === "en_progreso"
                        ? "#1e40af"
                        : "#92400e"
                  }}>
                    {estadoConfig?.value === "resuelto" && "✅ Completado"}
                    {estadoConfig?.value === "en_progreso" && "⏳ En proceso"}
                    {estadoConfig?.value === "pendiente" && "⏸️ Esperando revisión"}
                  </div>
                  <button
                    onClick={() => navigate(`/reclamo/${reclamo.id}`)}
                    style={{
                      padding: "6px 12px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#00a680",
                      backgroundColor: "transparent",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: "6px",
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f0fdf4";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    Ver Detalle →
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}